import type { NextApiRequest, NextApiResponse } from 'next';
import { z } from 'zod';

import { useSearchClient } from 'src/hooks/useSearchClient';

import { SearchResult, TypeaheadSearchResult } from 'src/types/model';

import { clean } from 'lib/searchblox';

type ResponseData = {
  results: TypeaheadSearchResult[];
};

type ErrorResponse = {
  error: unknown;
};

export const serviceTypeaheadValueSchema = z.object({
  href: z.string(),
  title: z.string(),
});

export type ServiceTypeaheadValue = z.infer<typeof serviceTypeaheadValueSchema>;

export default async function handler(
  req: NextApiRequest,
  res: NextApiResponse<ResponseData | ErrorResponse>
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  const querySchema = z.object({
    q: z.string().min(1, 'Query parameter "q" is required'),
    take: z
      .string()
      .optional()
      .transform((val) => (val ? parseInt(val, 10) : 10))
      .refine((val) => !isNaN(val) && val > 0, 'Invalid "take" parameter'),
  });

  const parseResult = querySchema.safeParse(req.query);

  if (!parseResult.success) {
    return res.status(400).json({ error: parseResult.error });
  }

  const { q, take } = parseResult.data;
  const query = clean(q)?.trim();

  const takeNumber = take ?? 10;

  try {
    const { searchClient } = useSearchClient();

    const searchResult = await searchClient.search<SearchResult>({
      query: `oni_posttitle:${query} OR oni_posttitle:*${query}* OR oni_posttitle:${query}~`,
      filter: 'oni_posttype: Services',
      pagesize: takeNumber,
    });

    const results = searchResult.result.map((result) => {
      const title = result.oni_posttitle;
      const url = new URL(result.url, process.env.BASE_URL).pathname;
      return {
        title,
        value: JSON.stringify({
          href: url,
          title,
        }),
      };
    });

    return res.status(200).json({ results });
  } catch (error) {
    console.error('Search error:', error);
    return res.status(500).json({ error: 'Internal server error' });
  }
}
